import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {Button, Checkbox, DatePicker, Input, Modal, Select, message} from 'antd'
import moment from 'moment'
import {Prompt} from 'react-router-dom'
import {getActualHours, saveActualHours} from '../../../../services/api/apiProductionPlanning'
import useUrlFilters from '../../../helpers/useUrlFilters'
import {
    ACTUAL_HOURS_MAX_DAYS,
    ACTUAL_HOURS_STEP,
    MIN_CHANGE_REASON,
    ROUTES,
} from '../../constants'
import {
    baselineOf,
    cellKey,
    diffEntries,
    missingCountOf,
    overwriteCountOf,
    parseHoursInput,
    shouldBypassPrompt,
} from '../../helpers/actualHours'
import {errorMessage} from '../../helpers/apiError'
import {canEditActualHours} from '../../helpers/permission'
import {recentMonthRanges} from '../../helpers/dateRange'
import {formatDecimal, formatInt} from '../../helpers/format'
import ActualHoursGrid, {HoursLegend} from './ActualHoursGrid'
import TimesheetImportModal from './TimesheetImportModal'
import LoadError from '../shared/LoadError'
import Notice from '../shared/Notice'

const {RangePicker} = DatePicker
const {TextArea} = Input

const DAY = 'YYYY-MM-DD'

/**
 * SCR Giờ làm thực tế (FAC-26417 FR-001) — nhập GIỜ CÓ MẶT của từng tài khoản theo từng ngày.
 *
 * Đây là MẪU SỐ của mọi chỉ số hiệu suất sắp thêm vào M18, nên màn này không phải một bảng nhập cho
 * đủ: ba ranh giới dưới đây quyết định báo cáo đúng hay sai.
 *
 * 1. **Ô trống ≠ 0 giờ.** Trống = chưa ai nhập, ngày đó bị LOẠI khỏi hiệu suất (BR-2.2). `0` = đã
 *    khai "hôm đó không làm", ngày đó NẰM TRONG mẫu số. Coi trống là 0 thì hiệu suất ra vô cực; coi
 *    0 là trống thì người nghỉ biến mất khỏi báo cáo. Xem `helpers/actualHours.js`.
 * 2. **Giờ ở đây là GIỜ CÔNG HR đã chốt**, cũng là giờ tính lương (PO câu 4).
 *    ⚠️ Từ 27/10/2026 nó KHÔNG còn là "giờ có mặt": nguồn là bảng chấm công tổng hợp, và bảng đó
 *    **chặn ở 8 giờ/ngày** (đo ba tháng thật: 89,6% số ô đúng 8, trong khi giờ quét thẻ cùng kỳ
 *    cao hơn trung vị 3,75h). Phần tăng ca không có trong nguồn này, nên ngày tăng ca cho hiệu
 *    suất cao hơn thực tế. Giải lao đã được gỡ hẳn — con số HR đưa đã là giờ công ròng.
 * 3. **Ngày là ngày lịch giờ VN**, service cắt bằng UTC+7 cố định. Màn này gửi `YYYY-MM-DD` chứ
 *    không gửi mốc ISO — xem ghi chú ở `getActualHours`.
 */
function ActualHoursPage({history, location}) {
    /*
     * Mặc định tính ở MOUNT chứ không ở mức module: khai ngoài component thì "7 ngày gần đây" bị
     * đóng băng ở thời điểm nạp bundle, và một tab để mở qua đêm sẽ mở ra khoảng của hôm qua. Cùng
     * lý do `presetRanges()` trả HÀM chứ không trả giá trị dựng sẵn (`helpers/dateRange.js`).
     *
     * ⚠️ `useUrlFilters` chốt lấy tham chiếu `defaults` ở lần render ĐẦU, nên object này bắt buộc
     *    phải ổn định — `useMemo` rỗng dependency, không phải khai inline.
     */
    const defaults = useMemo(() => ({
        /*
         * NGÀY dạng `YYYY-MM-DD`, cố ý KHÔNG phải `moment`/ISO như các màn báo cáo khác của M18.
         * Service đọc tham số này là ngày lịch giờ VN; đẩy một mốc ISO vào thì nó cắt 10 ký tự đầu,
         * tức lấy ngày UTC, và mọi mốc từ 00:00–07:00 giờ VN lệch đúng một ngày.
         */
        from: moment().subtract(6, 'days').format(DAY),
        to: moment().format(DAY),
        actors: [],
        onlyMissing: false,
        onlyWithOutput: false,
    }), [])

    const [filters, setFilter] = useUrlFilters(defaults, {history, location})

    const [grid, setGrid] = useState({days: [], actors: [], missingCount: 0})
    const [baseline, setBaseline] = useState({})
    const [edits, setEdits] = useState({})
    const [errors, setErrors] = useState({})
    /**
     * CHỮ của những ô bị từ chối, giữ ở tầng CHA.
     *
     * ⚠️ Lưới có ảo hoá dòng, nên dòng cuộn ra khỏi khung nhìn là UNMOUNT. Trạng thái chữ nằm trong
     *    `HoursCell` mất theo, còn `errors` thì ở đây — cuộn trở lại, ô dựng lại từ `value` và hiện
     *    một con số HỢP LỆ kèm viền đỏ với dòng "Tối đa 24 giờ". Tệ hơn: `invalidCount > 0` khoá nút
     *    Lưu, mà `edits` rỗng nên `dirty` sai và nút Huỷ cũng bị khoá — người dùng KẸT, lối ra duy
     *    nhất là tự tìm đúng ô đỏ đó mà gõ lại. (Vòng review 2.)
     */
    const [invalidText, setInvalidText] = useState({})
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const [saving, setSaving] = useState(false)
    const [importOpen, setImportOpen] = useState(false)
    const [reasonOpen, setReasonOpen] = useState(false)
    const [reason, setReason] = useState('')

    /*
     * Danh sách tài khoản cho ô lọc, lấy từ lần tải KHÔNG lọc gần nhất.
     *
     * Không có endpoint danh sách nhân sự, nên nguồn duy nhất là chính lưới. Nhưng lấy từ lưới ĐANG
     * lọc thì sau khi chọn một tài khoản, ô lọc chỉ còn đúng tài khoản đó — không bỏ chọn thêm ai
     * được nữa, người dùng phải xoá URL để thoát.
     */
    const [actorOptions, setActorOptions] = useState([])

    // Dựng MỘT lần: `recentMonthRanges` trả hàm cho từng khoảng nên mốc vẫn tính lúc bấm, còn
    // bảng nhãn thì không có lý do gì phải dựng lại sau mỗi phím gõ vào lưới.
    const monthPresets = useMemo(() => recentMonthRanges(), [])

    const readOnly = !canEditActualHours()
    const canEdit = !readOnly
    const dayCount = useMemo(
        () => moment(filters.to, DAY).diff(moment(filters.from, DAY), 'days') + 1,
        [filters.from, filters.to],
    )
    const rangeInvalid = !moment(filters.from, DAY).isValid() || !moment(filters.to, DAY).isValid() || dayCount < 1
    const rangeTooWide = dayCount > ACTUAL_HOURS_MAX_DAYS

    const entries = useMemo(() => diffEntries(baseline, edits), [baseline, edits])
    const dirty = entries.length > 0
    /*
     * ⚠️ Còn ô sai cũng là "có gì để huỷ". `edits` KHÔNG nhận giá trị sai (có chủ đích), nên chỉ
     *    dựa vào `dirty` là khoá luôn lối thoát duy nhất của người dùng.
     */
    const canReset = entries.length > 0 || Object.keys(errors).length > 0
    const invalidCount = Object.keys(errors).length
    const overwrites = useMemo(() => overwriteCountOf(baseline, entries), [baseline, entries])
    const missingNow = useMemo(
        () => missingCountOf(grid.actors, grid.days, edits),
        [grid.actors, grid.days, edits],
    )

    const fetchGrid = useCallback(async () => {
        /*
         * Khoảng không hợp lệ thì KHÔNG gọi API — nhưng phải tắt `loading`, nếu không bảng quay
         * vòng vĩnh viễn ngay dưới dải báo lỗi và người dùng tưởng màn hình treo chứ không đọc
         * thấy câu bảo họ thu hẹp khoảng lại.
         */
        if (rangeInvalid || rangeTooWide) {
            setLoading(false)

            return
        }

        setLoading(true)
        setError(null)

        try {
            const data = await getActualHours({
                from: filters.from,
                to: filters.to,
                actors: filters.actors,
                onlyMissing: filters.onlyMissing ? 1 : undefined,
                onlyWithOutput: filters.onlyWithOutput ? 1 : undefined,
            })

            setGrid(data)
            setBaseline(baselineOf(data.actors))
            setEdits({})
            setErrors({})
            setInvalidText({})

            // Chỉ làm mới danh sách lọc khi đang xem TOÀN BỘ — xem ghi chú ở `actorOptions`.
            if (!filters.actors.length && !filters.onlyMissing && !filters.onlyWithOutput) {
                setActorOptions(data.actors.map((row) => row.actor))
            }
        } catch (e) {
            setError(e)
        } finally {
            setLoading(false)
        }
    }, [
        filters.from,
        filters.to,
        filters.actors,
        filters.onlyMissing,
        filters.onlyWithOutput,
        rangeInvalid,
        rangeTooWide,
    ])

    useEffect(() => {
        fetchGrid()
    }, [fetchGrid])

    /*
     * Chặn đóng tab / tải lại trang khi còn ô chưa lưu.
     *
     * Lưới một tuần là hàng trăm ô; gõ xong cả buổi rồi lỡ tay F5 là mất sạch, và không có bản nháp
     * nào ở đâu để lấy lại. Trình duyệt chỉ cho hiện hộp thoại mặc định của nó — đủ để dừng tay.
     */
    const dirtyRef = useRef(dirty)
    /** Lượt điều hướng do chính màn hình khởi tạo và đã hỏi rồi — `Prompt` không hỏi lại. */
    const bypassRef = useRef(false)

    dirtyRef.current = dirty

    /*
     * ⚠️ Lưới chắn thứ HAI cho `bypassRef`: hễ người dùng gõ thêm một ô là mọi lượt "đã hỏi rồi"
     *    trước đó hết hiệu lực.
     *
     *    Cần nó vì có một ca mà cờ bật đúng nhưng không ai tiêu thụ: người dùng đang dở tay bấm
     *    "Nhập từ file", xác nhận, rồi đóng modal lại mà không nhập gì. Lượt đó không điều hướng
     *    (nay `navigates: false` nên không bật cờ), nhưng để chắc chắn không còn đường nào khác
     *    làm cờ nằm lại qua một phiên gõ mới, ràng buộc được phát biểu ở đây thay vì phải nhớ.
     */
    useEffect(() => {
        bypassRef.current = false
    }, [edits])

    useEffect(() => {
        const onBeforeUnload = (event) => {
            if (!dirtyRef.current) return undefined

            event.preventDefault()
            event.returnValue = ''

            return ''
        }

        window.addEventListener('beforeunload', onBeforeUnload)

        return () => window.removeEventListener('beforeunload', onBeforeUnload)
    }, [])

    /** Đổi bộ lọc khi còn ô chưa lưu = nạp lại lưới = mất hết. Hỏi trước. */
    /**
     * Chạy `action`, hỏi trước nếu còn ô chưa lưu (EC-O-05).
     *
     * ⚠️ `beforeunload` KHÔNG đủ. Nó chỉ bắt lúc đóng tab hay tải lại trang; ba đường còn lại —
     *    đổi bộ lọc, mở modal nhập file (lượt nhập xong gọi `fetchGrid`, mà `fetchGrid` gọi
     *    `setEdits({})`), và bấm nút sang màn khác — đều đi qua React và đều XOÁ SẠCH các ô vừa gõ
     *    mà không hỏi gì. Người dùng gõ cả tuần, bấm "Nhập từ file" để bổ sung thêm, và mất hết.
     */
    /**
     * Chạy `action`, hỏi trước nếu còn ô chưa lưu (EC-O-05).
     *
     * ⚠️ `navigates` KHÔNG phải tuỳ chọn cho đẹp — nó quyết định có tắt `<Prompt>` hay không, và
     *    bật nhầm là mất hẳn lớp chặn.
     *
     *    Bản trước bật cờ VÔ ĐIỀU KIỆN, kể cả nhánh "chưa dirty". Lúc chưa dirty thì
     *    `<Prompt when={dirty}>` không gắn `history.block`, nên `message` — chỗ DUY NHẤT hạ cờ —
     *    không bao giờ chạy và cờ kẹt `true` vĩnh viễn. Luồng thường gặp nhất: mở màn → đổi tuần
     *    (cờ kẹt) → gõ giờ cả tuần → bấm menu trái → `Prompt` thấy cờ, cho đi luôn, **mất sạch ô
     *    vừa gõ mà không hỏi gì**. Tệ hơn bản chưa có `Prompt`, vì người dùng đã tin là có hộp hỏi.
     *
     *    Nay: chỉ bật trong nhánh ĐÃ HỎI, và chỉ cho action THẬT SỰ điều hướng. Mở modal nhập file
     *    không điều hướng nên không bật — nếu bật, cờ nằm lại chờ lượt rời màn kế tiếp.
     */
    const confirmIfDirty = useCallback((action, content, {navigates = false} = {}) => {
        if (!dirtyRef.current) {
            // Không có gì để mất ⇒ không hỏi, và cũng KHÔNG đụng tới cờ.
            action()

            return
        }

        Modal.confirm({
            title: 'Bỏ các ô chưa lưu?',
            content,
            okText: 'Bỏ và tiếp tục',
            cancelText: 'Ở lại',
            onOk: () => {
                /*
                 * Cờ được TIÊU THỤ trong chính `message` của `<Prompt>`, không hạ bằng đồng hồ:
                 * `setFilter` ghi URL ở passive effect còn `setTimeout` là một hàng đợi khác, không
                 * spec nào bảo đảm thứ tự giữa hai bên.
                 */
                // Vị từ ở `helpers/actualHours.js` — xem docblock ở đó, nó đã trượt hai lần.
                if (shouldBypassPrompt({dirty: dirtyRef.current, navigates})) bypassRef.current = true

                try {
                    action()
                } catch (error) {
                    // Ném giữa chừng mà cờ kẹt là mất hẳn `<Prompt>` cho cả phiên còn lại.
                    bypassRef.current = false
                    throw error
                }
            },
        })
    }, [])

    const guardedSetFilter = useCallback(
        (patch) => confirmIfDirty(
            () => setFilter(patch),
            'Đổi bộ lọc sẽ tải lại lưới. Những ô vừa nhập mà chưa bấm Lưu sẽ mất.',
            // `setFilter` ghi URL bằng `history.replace`, mà `history.block` chặn cả `replace`.
            {navigates: true}
        ),
        [confirmIfDirty, setFilter]
    )

    /** Rời màn bằng nút điều hướng — cùng một mất mát, cùng một câu hỏi. */
    const guardedGo = useCallback(
        (route) => confirmIfDirty(
            () => history.push(route),
            'Rời màn này sẽ bỏ những ô vừa nhập mà chưa bấm Lưu.',
            {navigates: true}
        ),
        [confirmIfDirty, history]
    )

    const onEdit = useCallback((actor, day, raw) => {
        const key = cellKey(actor, day)
        const parsed = parseHoursInput(raw)

        setInvalidText((prev) => {
            if (parsed.ok) {
                if (!(key in prev)) return prev

                const next = {...prev}

                delete next[key]

                return next
            }

            return {...prev, [key]: String(raw ?? '')}
        })

        setErrors((prev) => {
            if (parsed.ok && !prev[key]) return prev

            const next = {...prev}

            if (parsed.ok) {
                delete next[key]
            } else {
                next[key] = parsed.error
            }

            return next
        })

        /*
         * Ô sai KHÔNG ghi vào `edits`: `edits` là thứ sẽ được gửi lên service, nhét một giá trị
         * không hợp lệ vào đó là để nó lọt lên server nếu có đường nào bỏ qua `invalidCount`. Ô vẫn
         * hiện viền đỏ và chữ lỗi nhờ `errors`.
         */
        if (!parsed.ok) return

        setEdits((prev) => ({...prev, [key]: parsed.value}))
    }, [])

    const commit = useCallback(async (changeReason) => {
        setSaving(true)

        try {
            const result = await saveActualHours(entries, changeReason)

            message.success(
                `Đã lưu: ${formatInt(result.created)} ô mới, ${formatInt(result.updated)} ô sửa, `
                + `${formatInt(result.removed)} ô xoá.`,
            )
            setReasonOpen(false)
            setReason('')
            await fetchGrid()
        } catch (e) {
            // Giữ nguyên `edits` khi lỗi — xoá đi là người dùng mất công gõ lại cả lượt vì một ô sai.
            message.error(errorMessage(e) || 'Không lưu được giờ làm. Vui lòng thử lại')
        } finally {
            setSaving(false)
        }
    }, [entries, fetchGrid])

    const onSave = useCallback(() => {
        if (invalidCount > 0) {
            message.error(`Còn ${formatInt(invalidCount)} ô nhập sai, sửa xong mới lưu được`)

            return
        }

        // BR-1.4: chỉ hỏi lý do khi lượt ghi chạm ô ĐÃ CÓ giá trị. Hỏi cả lượt nhập đầu tiên của tuần
        // là biến thao tác hằng ngày thành thủ tục, và người dùng sẽ gõ "nhập giờ" vào mọi ô cho xong.
        if (overwrites > 0) {
            setReasonOpen(true)

            return
        }

        commit(undefined)
    }, [invalidCount, overwrites, commit])

    const rangeValue = [moment(filters.from, DAY), moment(filters.to, DAY)]

    return (
        <div className="VcPage VcActualHoursPage container-fluid">
            <div className="VcPageHead">
                <div>
                    <h1 className="VcPageTitle">Giờ làm thực tế</h1>
                    <div className="VcPageSubtitle">
                        Giờ công theo bảng chấm công của HR, từng tài khoản theo từng ngày — mẫu số của
                        mọi chỉ số hiệu suất. Bảng đó chặn ở 8 giờ/ngày nên đây KHÔNG phải giờ có mặt.
                        Ngày lịch tính theo giờ Việt Nam (GMT+7).
                    </div>
                </div>
                <div className="VcPageActions">
                    {/*
                      * Nút nhập file đặt cạnh lưới chứ không thành một màn riêng: nó ghi vào ĐÚNG
                      * lưới này, và sau khi nhập người dùng phải thấy ngay kết quả để soát. Tách
                      * sang màn khác là bắt họ tự đi tìm xem vừa nhập được gì.
                      */}
                    {canEdit && (
                        <Button
                            type="primary"
                            /*
                             * ⚠️ Lượt nhập file kết thúc bằng `fetchGrid`, mà `fetchGrid` gọi
                             *    `setEdits({})` — tức nhập file XOÁ SẠCH các ô đang gõ tay. Hỏi
                             *    TRƯỚC khi mở modal, không hỏi sau khi đã nhập xong.
                             */
                            onClick={() => confirmIfDirty(
                                () => setImportOpen(true),
                                'Nhập từ file sẽ tải lại lưới. Những ô vừa gõ tay mà chưa bấm Lưu sẽ mất.'
                            )}
                        >
                            Nhập từ file chấm công
                        </Button>
                    )}
                    <Button onClick={() => guardedGo(ROUTES.WORKER_ACCOUNTS)}>Tài khoản nhân viên</Button>
                    <Button onClick={() => guardedGo(ROUTES.OUTPUT_REPORT)}>
                        Sản lượng &amp; giờ định mức →
                    </Button>
                </div>
            </div>

            <div className="VcFilterBar">
                <div className="VcField">
                    <label>Khoảng ngày</label>
                    <RangePicker
                        value={rangeValue[0].isValid() && rangeValue[1].isValid() ? rangeValue : null}
                        format="DD/MM/YYYY"
                        allowClear={false}
                        /*
                          * Preset theo THÁNG LỊCH là thứ màn này cần nhất: người đi nhập cầm file
                          * `CCT9.xlsx` của HR trên tay, họ cần đúng 01/09–30/09 chứ không cần một
                          * cửa sổ trượt. Bốn tháng gần nhất phủ gần hết việc nhập bù.
                          */
                        ranges={monthPresets}
                        // Không cho chọn ngày tương lai: service từ chối ghi (BR-1.7) nên cột tương
                        // lai chỉ là cột trống không bấm được — chọn được nó là mời người dùng làm
                        // một việc chắc chắn hỏng.
                        disabledDate={(date) => date && date.isAfter(moment(), 'day')}
                        onChange={(value) => value && guardedSetFilter({
                            from: value[0].format(DAY),
                            to: value[1].format(DAY),
                        })}
                    />
                </div>

                <div className="VcField">
                    <label>Tài khoản</label>
                    <Select
                        mode="tags"
                        style={{minWidth: 280}}
                        value={filters.actors}
                        placeholder="Mọi tài khoản"
                        // `tags` chứ không phải `multiple`: danh sách gợi ý chỉ gồm tài khoản CÓ mặt
                        // trong khoảng đang xem, nên phải gõ tay được tên người nghỉ cả khoảng đó.
                        onChange={(value) => guardedSetFilter({actors: value})}
                    >
                        {actorOptions.map((actor) => (
                            <Select.Option key={actor} value={actor}>{actor}</Select.Option>
                        ))}
                    </Select>
                </div>

                <div className="VcFilterChecks">
                    {/*
                      * Hai ô độc lập, KHÔNG phải một bộ chọn một-trong-hai: "còn thiếu giờ" là
                      * danh sách việc phải làm, "có sản lượng" là "ai thật sự có đi làm".
                      *
                      * ⚠️ Tập "còn thiếu giờ" nằm TRỌN trong tập "có sản lượng" (ô thiếu giờ theo
                      *    định nghĩa đã phải có sản lượng), nên tích cả hai cho kết quả y hệt tích
                      *    mình ô đầu. Không phải lỗi — đừng "sửa" thành loại trừ nhau.
                      */}
                    <Checkbox
                        checked={filters.onlyMissing}
                        onChange={(e) => guardedSetFilter({onlyMissing: e.target.checked})}
                    >
                        Chỉ tài khoản còn thiếu giờ
                    </Checkbox>
                    <Checkbox
                        checked={filters.onlyWithOutput}
                        onChange={(e) => guardedSetFilter({onlyWithOutput: e.target.checked})}
                    >
                        Chỉ tài khoản có sản lượng
                    </Checkbox>
                </div>
            </div>

            {rangeInvalid && (
                <Notice level="error">Khoảng ngày không hợp lệ: ngày kết thúc phải từ ngày bắt đầu trở đi.</Notice>
            )}

            {rangeTooWide && (
                <Notice level="error">
                    <div>
                        Khoảng đang chọn {formatInt(dayCount)} ngày, tối đa {ACTUAL_HOURS_MAX_DAYS} ngày
                        một lượt.
                    </div>
                    <div className="VcNoticeSub">
                        Mỗi ngày là một cột có ô nhập; rộng hơn thì bảng không nhập được. Thu hẹp lại
                        rồi xem tiếp.
                    </div>
                </Notice>
            )}

            {readOnly && (
                <Notice level="info">
                    Bạn đang ở chế độ chỉ xem. Nhập và sửa giờ làm cần quyền quản trị nhà máy
                    (<code>fac_admin</code>).
                </Notice>
            )}

            <Notice level="info">
                <div>
                    Ô TRỐNG nghĩa là <strong>chưa nhập</strong> — ngày đó bị loại khỏi chỉ số hiệu suất
                    nhưng sản lượng vẫn tính đủ. Gõ <strong>0</strong> nghĩa là{' '}
                    <strong>đã khai: hôm đó không làm</strong>, và ngày đó nằm trong mẫu số. Hai thứ
                    khác nhau, đừng dùng 0 để đánh dấu ô chưa biết.
                </div>
                <div>
                    Giờ ở đây là <strong>giờ công theo bảng chấm công của HR</strong> — cũng là giờ
                    tính lương. Bảng đó chặn ở <strong>8 giờ/ngày</strong> và đã trừ sẵn nghỉ giữa
                    ca, nên phần tăng ca không nằm trong con số này và ngày tăng ca sẽ cho hiệu suất
                    cao hơn thực tế.
                </div>
                <div>
                    Nhập tròn đến <strong>phút</strong>, tối đa 24 giờ một ngày. Mũi tên lên/xuống đi
                    từng {formatDecimal(ACTUAL_HOURS_STEP, 1)} giờ cho nhanh, nhưng gõ tay số lẻ hơn
                    vẫn được — bảng HR có ô lẻ tới phần tư giờ.
                </div>
            </Notice>

            {missingNow > 0 && !loading && (
                <Notice level="warning">
                    Còn {formatInt(missingNow)} ô có sản lượng nhưng chưa có giờ làm. Những ngày này
                    không vào được chỉ số hiệu suất.
                </Notice>
            )}

            {error ? (
                <LoadError error={error} onRetry={fetchGrid}/>
            ) : (
                <>
                    <HoursLegend/>

                    <ActualHoursGrid
                        days={grid.days}
                        rows={grid.actors}
                        edits={edits}
                        errors={errors}
                        invalidText={invalidText}
                        onEdit={onEdit}
                        readOnly={readOnly}
                        loading={loading}
                    />

                    {!readOnly && (
                        <div className="VcHoursSaveBar">
                            <div className="VcHoursSaveBar__info">
                                {dirty
                                    ? `${formatInt(entries.length)} ô thay đổi chưa lưu`
                                      + (overwrites > 0 ? ` · ${formatInt(overwrites)} ô ghi đè giá trị cũ` : '')
                                    : 'Chưa có thay đổi nào'}
                                {invalidCount > 0 && (
                                    <span className="VcDanger"> · {formatInt(invalidCount)} ô nhập sai</span>
                                )}
                            </div>
                            <Button
                                disabled={!canReset || saving}
                                onClick={() => {
                                    setEdits({})
                                    setErrors({})
                                    setInvalidText({})
                                }}
                            >
                                Huỷ thay đổi
                            </Button>
                            <Button
                                type="primary"
                                loading={saving}
                                disabled={!dirty || invalidCount > 0}
                                onClick={onSave}
                            >
                                Lưu giờ làm
                            </Button>
                        </div>
                    )}
                </>
            )}

            {/*
              * Hỏi lý do CHỈ khi ghi đè (BR-1.4). Nút xác nhận khoá tới khi đủ `MIN_CHANGE_REASON`
              * ký tự — cùng ngưỡng service đang gác, để không ai gõ xong rồi mới ăn 400.
              */}
            <Modal
                title="Sửa giờ đã lưu — khai lý do"
                visible={reasonOpen}
                onCancel={() => setReasonOpen(false)}
                onOk={() => commit(reason.trim())}
                confirmLoading={saving}
                okText="Xác nhận lưu"
                cancelText="Quay lại"
                okButtonProps={{disabled: reason.trim().length < MIN_CHANGE_REASON}}
            >
                <div className="VcWarnBox">
                    Lượt lưu này ghi đè {formatInt(overwrites)} ô đã có giá trị. Giá trị cũ chỉ còn lại
                    trong nhật ký thay đổi, nên lý do là thứ duy nhất giải thích vì sao con số đổi.
                </div>

                <div className="VcField mt-3">
                    <label>Lý do thay đổi <span className="VcDanger">*</span></label>
                    <TextArea
                        rows={3}
                        value={reason}
                        placeholder={`Tối thiểu ${MIN_CHANGE_REASON} ký tự`}
                        onChange={(event) => setReason(event.target.value)}
                    />
                </div>
            </Modal>

            {/*
              * Chốt chặn CUỐI cho mọi đường rời màn mà trang này không tự biết: menu bên trái, nút
              * Back của trình duyệt, một liên kết ở đâu đó. `Prompt` của react-router v5 chặn ngay
              * trong router nên nó bắt được cả ba.
              */}
            <Prompt
                when={dirty}
                /*
                 * Hàm trả `true` = cho đi. `Prompt` vì thế chỉ còn gác đúng phần nó sinh ra để gác:
                 * menu bên trái, nút Back của trình duyệt, một liên kết ở đâu đó — những đường mà
                 * màn hình này không tự biết và không tự hỏi được.
                 */
                message={() => {
                    // Cờ dùng MỘT lần: tiêu thụ ngay tại đây thay vì chờ một `setTimeout` hạ hộ.
                    if (bypassRef.current) {
                        bypassRef.current = false

                        return true
                    }

                    return 'Còn ô giờ làm chưa lưu. Rời màn này sẽ mất những ô đó. Vẫn rời?'
                }}
            />

            <TimesheetImportModal
                visible={importOpen}
                onClose={() => setImportOpen(false)}
                onImported={fetchGrid}
            />
        </div>
    )
}

export default ActualHoursPage
